import { type Query, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';
import { createRoad, deleteRoad, updateRoad } from '@/shared/apis/road-management';
import {
  createSite,
  deleteSite,
  fetchDetailSite,
  fetchSiteList,
  updateSite,
} from '@/shared/apis/site-management';
import type { ApiResponse } from '../types/abstract-api-response';
import type { RoadData } from '../types/types-road-management';
import type { POSTSiteModel, PUTSiteModel, SiteData } from '../types/types-site-management';

interface RoadInputItem {
  site_id?: number;
  road_id?: number;
  name?: string;
  is_new?: boolean;
}

interface UpdateSiteRoadInput {
  siteInfo: SiteData;
  roadInputList: RoadInputItem[];
}

interface CreateSiteRoadInput {
  siteInfo: SiteData;
  roadInputList: RoadInputItem[];
}

interface DeleteSiteRoadInput {
  siteId: number;
  roadIdList: (number | undefined)[];
}

interface TSiteManagement {
  queryParams?: string;
  siteId: number | null;
  onUpdateSuccess?: (responseData: unknown) => void;
  onDeleteSuccess?: (responseData?: unknown) => void;
  onCreateSuccess?: (responseData: unknown) => void;
  refetchInterval?:
    | number
    | false
    | ((
        query: Query<ApiResponse<SiteData>, Error, ApiResponse<SiteData>, string[]>
      ) => number | false | undefined)
    | undefined;
}

const useSiteManagement = ({
  queryParams = '',
  siteId = null,
  onUpdateSuccess = (_responseData) => {},
  onDeleteSuccess = () => {},
  onCreateSuccess = (_responseData) => {},
  refetchInterval,
}: TSiteManagement) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  // Query to fetch site list
  const {
    data: siteListData,
    // error: siteListError,
    isLoading: isLoadingSites,
  } = useQuery({
    queryKey: ['siteListData', queryParams],
    queryFn: () => fetchSiteList(queryParams),
    staleTime: 1000 * 60 * 1,
  });

  // Query to fetch detailed user data
  const {
    data: detailSiteData,
    // error: detailSiteError,
    isLoading: isLoadingDetailSite,
  } = useQuery({
    queryKey: ['detailSiteData', siteId as unknown as string],
    queryFn: () => fetchDetailSite(siteId!),
    retry: false,
    enabled: !!siteId,
    refetchInterval: refetchInterval,
    refetchIntervalInBackground: true,
    initialData: {
      code: '-',
      message: '-',
      data: {
        number_road: -1,
        address: '-',
        description: '-',
        lat: -1,
        lng: -1,
        mapped_box: null,
        mapped_sensor: null,
        name: '-',
        registered_by: '-',
        registered_time: '-',
        site_id: -1,
        type: '-',
        updated_by: '-',
        updated_time: '-',
      },
    },
  });

  // Update Site
  const updateSiteMutation = useMutation({
    mutationFn: (siteData: PUTSiteModel) => updateSite(siteData),
    onSuccess: (responseData) => {
      queryClient.invalidateQueries({ queryKey: ['siteListData'] });
      queryClient.invalidateQueries({ queryKey: ['detailSiteData'] });
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('정상적으로 정보가 입력되었습니다.'), {
        callback() {
          onUpdateSuccess(responseData);
        },
      });
    },
    onError: (err) => {
      console.error('Error updating site:', err);
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(err.message));
    },
  });

  //Update Site + Road
  const updateSiteRoadMutation = useMutation({
    mutationFn: async ({ siteInfo, roadInputList }: UpdateSiteRoadInput) => {
      const requests: Promise<ApiResponse<unknown>>[] = [];
      console.log('updateSiteRoadMutation');
      console.log(siteInfo);
      console.log(roadInputList);

      if (siteInfo && Object.keys(siteInfo).length > 0) {
        const putSiteData: PUTSiteModel = {
          site_id: siteInfo.site_id,
          name: siteInfo.name,
          type: siteInfo.type,
          address: siteInfo.address,
          lat: siteInfo.lat,
          lng: siteInfo.lng,
          description: siteInfo.description ?? '',
        };
        requests.push(updateSite(putSiteData));
      }

      if (roadInputList && Object.keys(roadInputList).length > 0) {
        // 반복문을 통해 roadInputList의 각 항목에 대해 요청을 추가
        for (let i = 0; i < roadInputList.length; i++) {
          const roadInfo = roadInputList[i];
          if (!roadInfo) continue;
          console.log('roadInfo.is_new');
          console.log(roadInfo.is_new);

          //기존껀 update요청, 새로운정보는 create요청
          if (roadInfo.is_new === true) {
            // new road info - create
            requests.push(createRoad(roadInfo as unknown as RoadData));
          } else if (roadInfo.is_new === false) {
            // exist road info - update
            // 각 roadInfo에 대해 updateSite 요청을 추가
            requests.push(updateRoad(roadInfo.road_id as number, roadInfo as unknown as RoadData));
          }
        }
      }
      return await Promise.all(requests);
    },

    onSuccess: (responseData) => {
      queryClient.invalidateQueries({ queryKey: ['siteListData'] });
      queryClient.invalidateQueries({ queryKey: ['detailSiteData'] });
      queryClient.invalidateQueries({ queryKey: ['roadListData'] });
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('정상적으로 정보가 입력되었습니다.'), {
        callback() {
          onUpdateSuccess(responseData);
        },
      });
    },
    onError: (err) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(err.message));
    },
  });

  // Delete Site
  const deleteSiteMutation = useMutation({
    mutationFn: (siteId: number) => deleteSite(siteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['siteListData'] });
      queryClient.invalidateQueries({ queryKey: ['detailSiteData'] });
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('정상적으로 삭제되었습니다.'), {
        callback() {
          onDeleteSuccess();
        },
      });
    },
    onError: (err) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(err.message));
    },
  });

  // Mutation to create a Site (POST request)
  const createSiteMutation = useMutation({
    mutationFn: async (siteData: POSTSiteModel) => {
      // async 키워드를 추가하여 비동기 처리
      console.log('createSiteMutation:'); // siteData 콘솔로 출력
      console.log('siteData:', siteData); // siteData 콘솔로 출력

      try {
        // 비동기 API 호출 후 결과를 기다림
        const createSiteResult = await createSite(siteData); // 실제 API 호출
        console.log('createSiteResult:', createSiteResult); // API 응답 출력

        return createSiteResult; // API 결과 반환
      } catch (error) {
        console.error('Error during createSite:', error);
        throw error; // 오류 발생 시 throw
      }
    },
    onSuccess: (responseData) => {
      queryClient.invalidateQueries({ queryKey: ['siteListData'] });
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('정상적으로 정보가 입력되었습니다.'), {
        callback() {
          onCreateSuccess(responseData);
        },
      });
      console.log('createSiteMutation on Success');
      console.log(responseData);
      return responseData;
    },
    onError: (err) => {
      console.error('Error creating site:', err);
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      // new NoticeMessage(t(err.message))
    },
  });

  const createSiteRoadMutation = useMutation({
    mutationFn: async ({ siteInfo, roadInputList }: CreateSiteRoadInput) => {
      const requests: Promise<ApiResponse<unknown>>[] = [];
      console.log('createSiteRoadMutation');

      console.log(siteInfo);
      console.log(roadInputList);

      if (siteInfo && Object.keys(siteInfo).length > 0) {
        const postSiteData: POSTSiteModel = {
          name: siteInfo.name,
          type: siteInfo.type,
          address: siteInfo.address,
          lat: siteInfo.lat,
          lng: siteInfo.lng,
          description: siteInfo.description ?? '',
        };
        requests.push(createSite(postSiteData));
      }

      if (roadInputList && Object.keys(roadInputList).length > 0) {
        // 반복문을 통해 roadInputList의 각 항목에 대해 요청을 추가
        for (let i = 0; i < roadInputList.length; i++) {
          const roadInfo = roadInputList[i];
          if (!roadInfo) continue;
          // 각 roadInfo에 대해 updateSite 요청을 추가
          requests.push(createRoad(roadInfo as unknown as RoadData));
        }
      }
      return await Promise.all(requests);
    },

    onSuccess: (responseData) => {
      queryClient.invalidateQueries({ queryKey: ['siteListData'] });
      queryClient.invalidateQueries({ queryKey: ['roadListData'] });
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('정상적으로 정보가 입력되었습니다.'), {
        callback() {
          onUpdateSuccess(responseData);
        },
      });
    },
    onError: (err) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(err.message));
    },
  });

  const deleteSiteRoadMutation = useMutation({
    mutationFn: async ({ siteId, roadIdList }: DeleteSiteRoadInput) => {
      const results: ApiResponse<unknown>[] = [];

      // 접근로를 먼저 삭제한 후 사이트를 삭제 (순서 보장)
      if (roadIdList && roadIdList.length > 0) {
        const validRoadIds = roadIdList.filter((id): id is number => id !== undefined && id !== 0);
        for (const roadId of validRoadIds) {
          const res = await deleteRoad(roadId);
          results.push(res);
        }
      }

      if (siteId) {
        const res = await deleteSite(siteId);
        results.push(res);
      }

      return results;
    },

    onSuccess: (responseData) => {
      queryClient.invalidateQueries({ queryKey: ['siteListData'] });
      queryClient.invalidateQueries({ queryKey: ['detailSiteData'] });
      queryClient.invalidateQueries({ queryKey: ['roadListData'] });
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('정상적으로 삭제되었습니다.'), {
        callback() {
          onDeleteSuccess(responseData);
        },
      });
    },
    onError: (err) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(err.message));
    },
  });

  return {
    siteListData,
    isLoadingSites,
    detailSiteData,
    isLoadingDetailSite,
    deleteSite: deleteSiteMutation.mutate,
    updateSite: updateSiteMutation.mutate,
    createSite: createSiteMutation.mutate,
    createSiteRoad: createSiteRoadMutation.mutate,
    updateSiteRoad: updateSiteRoadMutation.mutate,
    deleteSiteRoad: deleteSiteRoadMutation.mutate,
  };
};

export default useSiteManagement;
